import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import CustomButton from "../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    fabricantsResource,
    getTypes,
    modelsTruckTrailerResource,
    trailerTrucksResource,
} from "../../helpers/configRoutes/backend_helper";
import CustomInput from "../../components/form/CustomInput";
import { showFormErrors } from "../../helpers/showFormErrors";
import CustomSelect from "../../components/form/CustomSelect";
import AddCatalogsModal from "./AddCatalogsModal";
import { ToastHelper } from "../../helpers/configRoutes/alerts/alertHelper";

const AddUpdTrailerTruckModal = ({
    modal,
    setModal,
    trailerTruckItem,
    setFilters,
    filters,
    onUsageChanged,
}) => {
    const [models, setModels] = useState([]);
    const [types, setTypes] = useState([]);
    const [fabricants, setFabricants] = useState([]);
    const [modalCatalog, setModalCatalog] = useState({
        show: false,
        title: "Fabricant",
    });

    const [loading, setLoading] = useState(false);

    const isTruck = !!modal.truck;
    const trailerTypes = !isTruck ? types : [];

    const baseitem = {
        id: null,
        year: "",
        number: "",
        vin: "",
        ownership: "",
        current_mileage: "",
        plate_usd: "",
        expiration_usd: "",
        plate_mx: "",
        expiration_mx: "",
        inspection_date: "",
        expiration_assurance: "",
        type_id: "",
        fabricant_id: "",
        model_id: "",
        company_id: "",
        operator_id: "",
        truck: modal.truck,
    };

    const validation = useFormik({
        initialValues: trailerTruckItem || baseitem,
        enableReinitialize: true,
        validationSchema: Yup.object({
            number: Yup.string()
                .trim()
                .matches(/^[a-zA-Z0-9]+$/, {
                    message: isTruck
                        ? "Truck number must be alphanumeric"
                        : "Trailer number must be alphanumeric",
                    excludeEmptyString: true,
                })
                .required(
                    isTruck
                        ? "Truck number is required"
                        : "Trailer number is required"
                ),
            year: Yup.number()
                .typeError("The year must be a number")
                .integer("The year must be a valid integer")
                .min(1900, "Year must be after 1900")
                .max(
                    new Date().getFullYear(),
                    "Year must not be in the future"
                )
                .required("Year is required"),
            vin: Yup.string().required("VIN number is required"),
            current_mileage: Yup.string()
                .trim()
                .matches(/^\d+$/, "Mileage must contain only digits")
                .required("Mileage is required"),
            plate_usd: Yup.string().nullable(),
            expiration_usd: Yup.date()
                .nullable()
                .when("plate_usd", {
                    is: (val) => !!val,
                    then: (schema) =>
                        schema.required(
                            "USD expiration date is required when USD plate is filled"
                        ),
                }),
            plate_mx: Yup.string().nullable(),
            expiration_mx: Yup.date()
                .nullable()
                .when("plate_mx", {
                    is: (val) => !!val,
                    then: (schema) =>
                        schema.required(
                            "MX expiration date is required when MX plate is filled"
                        ),
                }),
            inspection_date: Yup.date().required(
                "Inspection date is required"
            ),
            expiration_assurance: Yup.date()
                .required("Assurance expiration date is required")
                .min(
                    new Date(),
                    "Assurance expiration date must be a future date"
                ),
            type_id: isTruck
                ? Yup.string().nullable()
                : Yup.string().required("Type is required"),
            model_id: Yup.string().required("Model is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await trailerTrucksResource(
                values.id ? "put" : "post",
                values,
                values.id
            );
            if (response.status) {
                if (typeof onUsageChanged === "function") {
                    await onUsageChanged();
                }
                setFilters({ ...filters, search: filters.search });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors || {});
                if (response.message && !response.errors) {
                    ToastHelper(response.message, response.feature_key ? "warning" : "error");
                }
            }
            setLoading(false);
        },
    });

    useEffect(() => {
        if (!trailerTruckItem?.id) {
            validation.resetForm({ values: baseitem });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal.show]);

    useEffect(() => {
        const getCatalogs = async () => {
            if (!isTruck) {
                const typesResponse = await getTypes({
                    params: { select: true, truck: isTruck },
                });
                setTypes(typesResponse.types || []);
            } else {
                setTypes([]);
            }

            const fabricantsResponse = await fabricantsResource("get", {
                params: {
                    select: true,
                    truck: isTruck,
                },
            });
            setFabricants(fabricantsResponse.fabricants || []);

        };
        getCatalogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTruck]);

    useEffect(() => {
        const getModels = async () => {
            const modelsResponse = await modelsTruckTrailerResource("get", {
                params: {
                    select: true,
                    fabricant_id: validation.values.fabricant_id,
                    truck: isTruck,
                },
            });
            setModels(modelsResponse.models || []);
        };
        if (validation.values.fabricant_id) {
            getModels();
        } else {
            setModels([]);
            validation.setFieldValue("model_id", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validation.values.fabricant_id, isTruck]);

    // Componente auxiliar para encabezados de sección
    const SectionHeader = ({ title }) => (
        <div className="w-full border-b border-cs-border pb-1 mb-3 mt-1">
            <h4 className="text-[11px] font-bold text-cs-primary uppercase tracking-wider">
                {title}
            </h4>
        </div>
    );

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            size={'max-w-4xl'}
            title={validation.values.id ? `Edit ${isTruck ? "Truck" : "Trailer"}` : `Register ${isTruck ? "Truck" : "Trailer"}`}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
                className="flex flex-col h-full"
            >
                <div className="bg-cs-bg rounded-2xl overflow-hidden shadow-xl flex flex-col max-h-[90vh]">

                    {/* --- HEADER --- */}
                    <div className="px-6 py-4 border-b border-cs-border bg-cs-surface z-10">
                        <div className="flex flex-col gap-1">
                            <p className="text-xs uppercase tracking-[0.2em] font-bold text-cs-primary-dark">
                                {isTruck ? "Truck Unit Management" : "Trailer Unit Management"}
                            </p>
                            <p className="text-[10px] text-cs-muted">
                                {validation.values.id ? "Editing unit details" : "Registering a new unit"}
                            </p>
                        </div>
                    </div>

                    {/* --- BODY (SCROLLABLE) --- */}
                    <div className="p-6 bg-cs-bg-alt overflow-y-auto custom-scrollbar">

                        {/* SECTION 1: GENERAL INFO */}
                        <SectionHeader title="General Information" />
                        <div className={`grid grid-cols-1 ${isTruck ? "md:grid-cols-4" : "md:grid-cols-5"} gap-5 mb-6`}>
                            <CustomInput
                                label={isTruck ? "Truck number" : "Trailer number"}
                                type="text"
                                name="number"
                                
                                
                                labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                                error={validation?.errors?.number}
                                value={validation?.values?.number || ""}
                                handleChange={validation.handleChange}
                            />
                            {!isTruck && (
                                <CustomSelect
                                    label="Unit Type"
                                    options={trailerTypes}
                                    name="type_id"
                                    labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                    error={validation?.errors?.type_id}
                                    value={validation?.values?.type_id}
                                    handleChange={validation.handleChange}
                                />
                            )}
                            <CustomInput
                                label="Year"
                                type="number"
                                name="year"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.year}
                                value={validation?.values?.year || ""}
                                handleChange={validation.handleChange}
                                className="text-[11px]"
                            />
                            <CustomInput
                                label="Ownership"
                                type="text"
                                name="ownership"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.ownership}
                                value={validation?.values?.ownership || ""}
                                handleChange={validation.handleChange}
                                className="text-[11px]"
                            />
                            <CustomInput
                                label="Mileage"
                                type="text"
                                name="current_mileage"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.current_mileage}
                                value={validation?.values?.current_mileage || ""}
                                handleChange={(e) => {
                                    const sanitized = e.target.value.replace(/\D/g, "");
                                    validation.setFieldValue("current_mileage", sanitized);
                                }}
                                className="text-[11px]"
                            />
                        </div>

                        {/* SECTION 2: SPECIFICATIONS (Fabricant & Model) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                            {/* Fabricant Wrapper */}
                            <div className="relative">
                                <div className="flex justify-between items-center mb-1.5">
                                    <label className="text-[10px] font-semibold text-cs-muted">Manufacturer</label>
                                    <span
                                        onClick={() => setModalCatalog({ show: true, title: "Fabricant" })}
                                        className="text-[9px] text-cs-primary cursor-pointer hover:underline font-medium flex items-center gap-1"
                                    >
                                        + New manufacturer
                                    </span>
                                </div>
                                <CustomSelect
                                    options={fabricants}
                                    name="fabricant_id"
                                    label={true}
                                    error={validation?.errors?.fabricant_id}
                                    value={validation?.values?.fabricant_id}
                                    handleChange={validation.handleChange}
                                />
                            </div>

                            {/* Model Wrapper */}
                            <div className="relative">
                                <div className="flex justify-between items-center mb-1.5">
                                    <label className="text-[10px] font-semibold text-cs-muted">Model</label>
                                    <span
                                        onClick={() => setModalCatalog({ show: true, title: "Model" })}
                                        className="text-[9px] text-cs-primary cursor-pointer hover:underline font-medium flex items-center gap-1"
                                    >
                                        + New Model
                                    </span>
                                </div>
                                <CustomSelect
                                    options={models}
                                    name="model_id"
                                    label={true}
                                    className="text-[11px]"
                                    error={validation?.errors?.model_id}
                                    value={validation?.values?.model_id}
                                    handleChange={validation.handleChange}
                                />
                            </div>
                        </div>

                        {/* SECTION 3: COMPLIANCE & DOCS */}
                        <SectionHeader title="Compliance & Documentation" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                            <CustomInput
                                label="VIN Number"
                                type="text"
                                name="vin"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.vin}
                                value={validation?.values?.vin || ""}
                                handleChange={validation.handleChange}
                                className="text-[11px] font-mono"
                            />
                            <CustomInput
                                label="Inspection Date"
                                type="date"
                                name="inspection_date"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.inspection_date}
                                value={validation?.values?.inspection_date || ""}
                                handleChange={validation.handleChange}
                                className="text-[11px]"
                            />
                            <CustomInput
                                label="Assurance Expiration"
                                type="date"
                                name="expiration_assurance"
                                labelClassName="text-[10px] font-semibold text-cs-muted mb-1.5"
                                error={validation?.errors?.expiration_assurance}
                                value={validation?.values?.expiration_assurance || ""}
                                handleChange={validation.handleChange}
                                className="text-[11px]"
                            />
                        </div>

                        {/* SECTION 4: PLATES (Split View) */}
                        <SectionHeader title="Registration & Plates" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* MX BLOCK */}
                            <div className="bg-white/50 p-4 rounded-xl border border-cs-border/60">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">MX</span>
                                    <span className="text-[10px] font-medium text-cs-muted">Mexico Registration</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <CustomInput
                                        label="Plate Number"
                                        type="text"
                                        name="plate_mx"
                                        placeholder="MX-000"
                                        labelClassName="text-[9px] font-medium text-cs-muted mb-1"
                                        error={validation?.errors?.plate_mx}
                                        value={validation?.values?.plate_mx || ""}
                                        handleChange={validation.handleChange}
                                        className="text-[11px]"
                                    />
                                    <CustomInput
                                        label="Expiration Date"
                                        type="date"
                                        name="expiration_mx"
                                        labelClassName="text-[9px] font-medium text-cs-muted mb-1"
                                        error={validation?.errors?.expiration_mx}
                                        value={validation?.values?.expiration_mx || ""}
                                        handleChange={validation.handleChange}
                                        className="text-[11px]"
                                    />
                                </div>
                            </div>

                            {/* USA BLOCK */}
                            <div className="bg-white/50 p-4 rounded-xl border border-cs-border/60">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded">USA</span>
                                    <span className="text-[10px] font-medium text-cs-muted">USA Registration</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <CustomInput
                                        label="Plate Number"
                                        type="text"
                                        name="plate_usd"
                                        placeholder="USA-000"
                                        labelClassName="text-[9px] font-medium text-cs-muted mb-1"
                                        error={validation?.errors?.plate_usd}
                                        value={validation?.values?.plate_usd || ""}
                                        handleChange={validation.handleChange}
                                        className="text-[11px]"
                                    />
                                    <CustomInput
                                        label="Expiration Date"
                                        type="date"
                                        name="expiration_usd"
                                        labelClassName="text-[9px] font-medium text-cs-muted mb-1"
                                        error={validation?.errors?.expiration_usd}
                                        value={validation?.values?.expiration_usd || ""}
                                        handleChange={validation.handleChange}
                                        className="text-[11px]"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* --- FOOTER --- */}
                    <div className="px-6 py-4 bg-cs-bg border-t border-cs-border flex justify-end gap-3 z-10">
                        <button
                            type="button"
                            onClick={() => setModal({ ...modal, show: false })}
                            className="px-4 py-2 rounded-xl text-[10px] font-medium text-cs-muted bg-white border border-cs-border hover:bg-cs-bg-alt transition-all"
                        >
                            Cancel
                        </button>
                        <CustomButton
                            loading={loading}
                            textLoad="Saving..."
                            type="success"
                            className="px-6 py-2 rounded-xl bg-cs-primary hover:bg-cs-primary-hover text-white text-[10px] font-bold shadow-md hover:shadow-lg transition-all"
                        >
                            {validation.values?.id
                                ? "Update Unit"
                                : `Register ${isTruck ? "Truck" : "Trailer"}`}
                        </CustomButton>
                    </div>
                </div>
            </form>

            <AddCatalogsModal
                modal={modalCatalog}
                setModal={setModalCatalog}
                validation={validation}
                setModels={setModels}
                setFabricants={setFabricants}
                isTruck={isTruck}
            />
        </BaseModalAnimated>
    );
};

export default AddUpdTrailerTruckModal;